/**
 * @license Copyright (c) 2003-2022, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

// The editor creator to use.
import { Alignment } from '@ckeditor/ckeditor5-alignment/src/alignment';
import { Autoformat } from '@ckeditor/ckeditor5-autoformat/src/autoformat';
import { Bold, Code, Italic, Strikethrough, Subscript, Superscript, Underline } from '@ckeditor/ckeditor5-basic-styles';
import { BlockQuote } from '@ckeditor/ckeditor5-block-quote/src/blockquote';
import { CodeBlock } from '@ckeditor/ckeditor5-code-block/src/codeblock';
import { ClassicEditor as ClassicEditorBase } from '@ckeditor/ckeditor5-editor-classic/src/classiceditor';
import { Essentials } from '@ckeditor/ckeditor5-essentials/src/essentials';
import { FindAndReplace } from '@ckeditor/ckeditor5-find-and-replace/src/findandreplace';
import { FontBackgroundColor } from '@ckeditor/ckeditor5-font/src/fontbackgroundcolor';
import { FontColor } from '@ckeditor/ckeditor5-font/src/fontcolor';
import { FontSize } from '@ckeditor/ckeditor5-font/src/fontsize';
import { Heading } from '@ckeditor/ckeditor5-heading/src/heading';
import { HorizontalLine } from '@ckeditor/ckeditor5-horizontal-line/src/horizontalline';
import { HtmlEmbed } from '@ckeditor/ckeditor5-html-embed/src/htmlembed';
import { GeneralHtmlSupport } from '@ckeditor/ckeditor5-html-support/src/generalhtmlsupport';
import { Image } from '@ckeditor/ckeditor5-image/src/image';
import { ImageCaption } from '@ckeditor/ckeditor5-image/src/imagecaption';
import { ImageInsert } from '@ckeditor/ckeditor5-image/src/imageinsert';
import { ImageResize } from '@ckeditor/ckeditor5-image/src/imageresize';
import { ImageStyle } from '@ckeditor/ckeditor5-image/src/imagestyle';
import { ImageToolbar } from '@ckeditor/ckeditor5-image/src/imagetoolbar';
import { ImageUpload } from '@ckeditor/ckeditor5-image/src/imageupload';
import { Indent } from '@ckeditor/ckeditor5-indent/src/indent';
import { IndentBlock } from '@ckeditor/ckeditor5-indent/src/indentblock';
import CKEditorInspector from '@ckeditor/ckeditor5-inspector';
import { List } from '@ckeditor/ckeditor5-list/src/list';
import { ListProperties } from '@ckeditor/ckeditor5-list/src/listproperties';
import { MediaEmbed } from '@ckeditor/ckeditor5-media-embed/src/mediaembed';
import { MediaEmbedToolbar } from '@ckeditor/ckeditor5-media-embed/src/mediaembedtoolbar';
import { PageBreak } from '@ckeditor/ckeditor5-page-break/src/pagebreak';
import { Paragraph } from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import { PasteFromOffice } from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice';
import { RemoveFormat } from '@ckeditor/ckeditor5-remove-format/src/removeformat';
import { SimpleUploadAdapter } from '@ckeditor/ckeditor5-upload/src/adapters/simpleuploadadapter';
import { SourceEditing } from '@ckeditor/ckeditor5-source-editing/src/sourceediting';
import { SpecialCharacters } from '@ckeditor/ckeditor5-special-characters/src/specialcharacters';
import { SpecialCharactersArrows } from '@ckeditor/ckeditor5-special-characters/src/specialcharactersarrows';
import { SpecialCharactersCurrency } from '@ckeditor/ckeditor5-special-characters/src/specialcharacterscurrency';
import { SpecialCharactersEssentials } from '@ckeditor/ckeditor5-special-characters/src/specialcharactersessentials';
import { SpecialCharactersText } from '@ckeditor/ckeditor5-special-characters/src/specialcharacterstext';
import { Table } from '@ckeditor/ckeditor5-table/src/table';
import { TableCaption } from '@ckeditor/ckeditor5-table/src/tablecaption';
import { TableCellProperties } from '@ckeditor/ckeditor5-table/src/tablecellproperties';
import { TableProperties } from '@ckeditor/ckeditor5-table/src/tableproperties';
import { TableToolbar } from '@ckeditor/ckeditor5-table/src/tabletoolbar';
import { Template } from '@ckeditor/ckeditor5-template';
import { TextTransformation } from '@ckeditor/ckeditor5-typing/src/texttransformation';
import { WordCount } from '@ckeditor/ckeditor5-word-count/src/wordcount';
// Custom ROC build
import InsertRocAsset from './plugins/ckeditor5-roc-asset/src/insertRocAsset.js';
import { Inspect, EditHelp, ShowExtended } from './plugins/ckeditor5-roc-misc/src/index.js';
import LinkImage from './plugins/ckeditor5-roc-link/src/linkimage.js';
import RocLink from './plugins/ckeditor5-roc-link/src/roclink.js';
import RocMediaEmbedTitle from './plugins/ckeditor5-roc-media-embed-title/rocMediaEmbedTitle.js';
import RocMediaEmbedTitleUI from './plugins/ckeditor5-roc-media-embed-title/rocMediaEmbedTitleUI.js';
import RocUploadAdapter from './plugins/ckeditor5-roc-upload-adapter/src/rocUploadAdapter.js';
import PopulateTestIds from './plugins/ckeditor5-roc-misc/src/populatetestids.js';

/* global document, window */
const myprompt = prompt;
const myconsole = console;

export default class ClassicEditor extends ClassicEditorBase {
	attachInspector(attach) {
		if (attach) {
			this.editorName = CKEditorInspector.attach(this);
		} else {
			//CKEditorInspector.detach(this.editorName[0]);
			CKEditorInspector.destroy();
		}
	}
	isSourceEditing() {
		return this.plugins.get('SourceEditing').isSourceEditingMode;
	}
	setSourceEditing(toggle) {
		this.plugins.get('SourceEditing').isSourceEditingMode = toggle;
	}
}

// Plugins to include in the build.
ClassicEditor.builtinPlugins = [
	Alignment,
	Autoformat,
	BlockQuote,
	Bold,
	Code,
	CodeBlock,
	List,
	ListProperties,
	Essentials,
	EditHelp,
	FindAndReplace,
	FontBackgroundColor,
	FontColor,
	FontSize,
	GeneralHtmlSupport,
	Heading,
	HorizontalLine,
	HtmlEmbed,
	Image,
	ImageCaption,
	ImageInsert,
	ImageResize,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	Indent,
	IndentBlock,
	InsertRocAsset,
	Italic,
	LinkImage,
	MediaEmbed,
	MediaEmbedToolbar,
	RocMediaEmbedTitle,
	RocMediaEmbedTitleUI,
	PageBreak,
	Paragraph,
	PasteFromOffice,
	PopulateTestIds,
	RemoveFormat,
	RocLink,
	Inspect,
	SimpleUploadAdapter,
	RocUploadAdapter,
	ShowExtended,
	SourceEditing,
	SpecialCharacters,
	SpecialCharactersArrows,
	SpecialCharactersCurrency,
	SpecialCharactersEssentials,
	SpecialCharactersText,
	Strikethrough,
	Subscript,
	Superscript,
	Table,
	TableCaption,
	TableCellProperties,
	TableProperties,
	TableToolbar,
	TextTransformation,
	Underline,
	WordCount,
	Template,
];

// Editor configuration.
ClassicEditor.defaultConfig = {
	toolbar: {
		items: [
			'insertTemplate',
			'|',
			'findAndReplace',
			'|',
			'heading',
			'|',
			'link',
			'roclink',
			'|',
			'imageInsert',
			'insertRocasset',
			'insertTable',
			'horizontalLine',
			'|',
			'codeBlock',
			'mediaEmbed',
			'htmlEmbed',
			'|',
			'sourceEditing',
			'|',
			'undo',
			'redo',
			'|',
			'showExtended',
			'inspect',
			'-',
			'editHelp',
			'fontSize',
			'fontColor',
			'fontBackgroundColor',
			'|',
			'bold',
			'italic',
			'strikethrough',
			'underline',
			'blockQuote',
			'|',
			'subscript',
			'superscript',
			'|',
			'specialCharacters',
			'|',
			'numberedList',
			'bulletedList',
			'|',
			'alignment',
			'|',
			'outdent',
			'indent',
			'|',
			'pageBreak',
			'|',
			'removeFormat',
		],
		shouldNotGroupWhenFull: true,
	},

	findAndReplace: {
		uiType: 'dropdown'
	},

	heading: {
		options: [
			{ model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
			{ model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
			{ model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
			{ model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
			{ model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
			{ model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
		],
	},

	language: 'en',

	image: {
		toolbar: [
			'imageStyle:inline',
			'imageStyle:block',
			'imageStyle:side',
			'|',
			'imageTextAlternative',
			'|',
			'setAssetDimensions',
			'linkImage',
			'rocLinkImage',
		],
	},
	mediaEmbed: {
		previewsInData: true,
	},
	table: {
		contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableCellProperties', 'tableProperties'],
	},

	link: {
		addTargetToExternalLinks: true,
		decorators: {
			openInNewTab: {
				mode: 'manual',
				label: 'Open in a new tab',
				attributes: {
					target: '_blank',
					rel: 'noopener noreferrer',
				},
			},
		},
	},

	rocasset: {
		editAction: (initialValue) => {
			if (initialValue) {
				const rocLinkData = JSON.parse(myprompt('add roc asset', JSON.stringify(initialValue)));
				if (rocLinkData && rocLinkData.rocasset)
					window.editor.execute('insertImage', {
						source: [
							{
								src: rocLinkData.src,
								alt: rocLinkData.alt,
								rocasset: rocLinkData.rocasset,
							},
						],
					});
			}
		},

		resizeAction: (initialValue) => {
			const rocDimensions = JSON.parse(myprompt('set dimensions', JSON.stringify(initialValue)));
			if (rocDimensions && rocDimensions.rocasset) {
				window.editor.execute('insertImage', {
					source: [
						{
							src: 'http://web.roccommerce.local:5000/assets/f9/84/f98414ea-7f67-42c9-8526-733efb7fc48d/d160x160-rainbow-love-heart-background-red-wood-60045149.jpg',
							alt: 'stuff',
							rocasset: rocDimensions.rocasset,
						},
					],
				});
			}
		},

		refreshAction: (oldValue) => {
			oldValue.altText = 'new alt text';
			return oldValue;
		},
	},

	roclink: {
		refreshAction: (oldValue) => {
			oldValue.title = 'new title';
			return oldValue;
		},

		followAction: (initialValue) => {
			if (initialValue) {
				myconsole.log(initialValue);
				return true;
			}
		},
		editAction: (initialValue) => {
			const rocLinkData = myprompt('add roc link', JSON.stringify(initialValue));
			if (rocLinkData) {
				window.editor.execute('link', '#', {
					linkRoclink: JSON.parse(rocLinkData),
				});
				myconsole.log(rocLinkData);
			}
		},
		decorator: {
			id: 'roclink',
			classes: [],
			attributes: {},
		},
	},

	uploadasset: {
		uploadAction: (initialValue, resolve, reject) => {},
	},

	edithelp: {
		callback: () => {
			window.open(
				'https://roccommerce.atlassian.net/wiki/spaces/PROG/pages/300925948199193/WYSIWYG+Modules',
				'_blank',
			);
		},
	},

	datatestids: {
		callback: () => {
			return {
				findAndReplace: 'ckeditor5-findAndReplace-button',
				heading: 'ckeditor5-heading-button',
				link: 'ckeditor5-link-button',
				roclink: 'ckeditor5-roclink-button',
				imageInsert: 'ckeditor5-imageInsert-button',
				insertRocasset: 'ckeditor5-insertRocasset-button',
				insertTable: 'ckeditor5-insertTable-button',
				horizontalLine: 'ckeditor5-horizontalLine-button',
				codeBlock: 'ckeditor5-codeBlock-button',
				mediaEmbed: 'ckeditor5-mediaEmbed-button',
				htmlEmbed: 'ckeditor5-htmlEmbed-button',
				sourceEditing: 'ckeditor5-sourceEditing-button',
				undo: 'ckeditor5-undo-button',
				redo: 'ckeditor5-redo-button',
				showExtended: 'ckeditor5-showExtended-button',
				inspect: 'ckeditor5-inspect-button',
				editHelp: 'ckeditor5-editHelp-button',
				fontSize: 'ckeditor5-fontSize-button',
				fontColor: 'ckeditor5-fontColor-button',
				fontBackgroundColor: 'ckeditor5-fontBackgroundColor-button',
				bold: 'ckeditor5-bold-button',
				italic: 'ckeditor5-italic-button',
				strikethrough: 'ckeditor5-strikethrough-button',
				underline: 'ckeditor5-underline-button',
				blockQuote: 'ckeditor5-blockQuote-button',
				subscript: 'ckeditor5-subscript-button',
				superscript: 'ckeditor5-superscript-button',
				specialCharacters: 'ckeditor5-specialCharacters-button',
				numberedList: 'ckeditor5-numberedList-button',
				bulletedList: 'ckeditor5-bulletedList-button',
				alignment: 'ckeditor5-alignment-button',
				outdent: 'ckeditor5-outdent-button',
				indent: 'ckeditor5-indent-button',
				pageBreak: 'ckeditor5-pageBreak-button',
				removeFormat: 'ckeditor5-removeFormat-button',
			};
		},
	},
	showextended: { expanded: true },

	inspect: {
		callback: (attach) => {
			if (window.editor) {
				window.editor.attachInspector(attach);
			}
		},
	},

	htmlSupport: {
		allow: [
			{
				name: /.*/,
				attributes: true,
				classes: true,
				styles: true,
			},
		],
	},
	// Updated license key for CKEditor's v47.6.0
	// Note: Development only license key
	licenseKey: 'eyJhbGciOiJFUzI1NiJ9.eyJleHAiOjE3NzcwNzUxOTksImp0aSI6IjM5MGI4YjgyLTg1NGUtNDc1Ny1iMDRlLTIwODFiNjU4OTJhZSIsImRpc3RyaWJ1dGlvbkNoYW5uZWwiOlsic2giLCJkcnVwYWwiXSwid2hpdGVMYWJlbCI6dHJ1ZSwibGljZW5zZVR5cGUiOiJkZXZlbG9wbWVudCIsImZlYXR1cmVzIjpbIkRSVVAiLCJETyIsIkZQIiwiU0MiLCJUT0MiLCJUUEwiLCJQT0UiLCJDQyIsIk1GIiwiU0VFIiwiRUNIIiwiRUlTIiwiTEgiLCJGT08iXSwidmMiOiJiMjI0NjA1NCJ9.O5SGx5huPWYdwWnyyllOwqQC1NwkZWkHc-TlVMZjI5LS3dDl4YBfW8PU02xi5FODGjx2ic2SKGiMxbzi0z7_sg',
};
